import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Clickable table header that toggles asc/desc sorting.
 *
 * Props:
 *   field        unique id (e.g. 'title', 'due_date')
 *   sort         { field, dir } current sort state
 *   onChange     (next: { field, dir }) => void
 *   align        'left' (default) | 'right'
 *   testId       optional data-testid
 *   children     header label
 */
export function SortableHeader({
  field,
  sort,
  onChange,
  align = "left",
  testId,
  children,
}) {
  const isActive = sort?.field === field;
  const dir = isActive ? sort.dir : null;

  const handleClick = () => {
    if (!isActive) {
      onChange({ field, dir: "asc" });
    } else if (dir === "asc") {
      onChange({ field, dir: "desc" });
    } else {
      // third click clears sort
      onChange({ field: null, dir: null });
    }
  };

  const Icon = !isActive ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;

  return (
    <button
      type="button"
      data-testid={testId || `sort-${field}`}
      onClick={handleClick}
      className={cn(
        "inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider transition-colors",
        align === "right" && "ml-auto",
        isActive
          ? "text-stone-900"
          : "text-stone-500 hover:text-stone-800",
      )}
    >
      <span>{children}</span>
      <Icon
        size={12}
        strokeWidth={2}
        className={cn(isActive ? "opacity-100" : "opacity-50")}
      />
    </button>
  );
}

/**
 * Generic sort comparator factory.
 *
 * `getValue(row)` should return a value extracted for the given field.
 * Strings are compared with Italian collation; numbers / dates compared natively.
 */
export function makeSorter(sort, getValue) {
  if (!sort?.field) return null;
  const dir = sort.dir === "desc" ? -1 : 1;
  return (a, b) => {
    const av = getValue(a, sort.field);
    const bv = getValue(b, sort.field);
    if (av === bv) return 0;
    if (av === null || av === undefined || av === "") return 1; // empties last
    if (bv === null || bv === undefined || bv === "") return -1;
    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * dir;
    }
    return (
      String(av).localeCompare(String(bv), "it", { sensitivity: "base" }) * dir
    );
  };
}
